import { useCallback, useState } from "react";

import type { LangCode, RoomNewItem_EditPageQuery } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import { NameOfResourceBeingEdited } from "@/components/features/_components";
import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithCategory,
} from "@/components/features/_layouts";

import { HeaderActions } from "./components/header/actions";
import { ProductNewRoomFormModelContextProvider } from "./context";
import { DialogConfirmContinueCreateRoom } from "../../../../_shared/ui/_components/dialog-confirm-continue-create-room";
import { useInitialized } from "../../model/usecase/new/use-init";
import { RoomFormMain } from "../_shared/components/root";
import { standardRoomCategoriesAndManagerStore } from "../_shared/store/categories";

type Props = {
	hotelID: string;
	activeLangCode: LangCode;
	serverModel: RoomNewItem_EditPageQuery;
};

export const NewRoomEditPresenter = ({ hotelID, activeLangCode, serverModel }: Props) => {
	const [openDialogConfirmContinueCreateRoom, setOpenDialogConfirmContinueCreateRoom] = useState(false);
	const handleOpenChange = useCallback((input: boolean) => setOpenDialogConfirmContinueCreateRoom(input), []);
	const onOpenDialogConfirmContinueCreateRoom = useCallback(() => setOpenDialogConfirmContinueCreateRoom(true), []);

	const onTransitionToCreateRoom = useCallback(() => {
		/**
		 * @todo 再生成のロジックを実装し、ソフトナビゲーションで遷移する
		 */
		window.location.reload();
	}, []);

	const onCreateRoomSuccessCallback = useCallback(() => {
		onOpenDialogConfirmContinueCreateRoom();
	}, [onOpenDialogConfirmContinueCreateRoom]);

	const init = useInitialized({ serverModel, onCreateRoomSuccessCallback });

	const { categoryManagerStore } = standardRoomCategoriesAndManagerStore;

	const { isDirty } = init.viewModel;
	useTransitionBlock(isDirty);

	const roomName = init.viewModel.name.value;

	return (
		<>
			<ProductNewRoomFormModelContextProvider value={{ ...init }}>
				<FeatureIslandContainer>
					<FeatureIslandHeader
						title="新規部屋の作成"
						activeLangCode={activeLangCode}
						rightChildren={<HeaderActions />}
					/>
					<FeatureIslandMainWithCategory categoryManagerStore={categoryManagerStore}>
						{/* @todo Layout の共通化が出来るまでは共通化しない */}
						<NameOfResourceBeingEdited resourceName={roomName} />

						<RoomFormMain roomCategoriesAndManagerStore={standardRoomCategoriesAndManagerStore} />
					</FeatureIslandMainWithCategory>
				</FeatureIslandContainer>
			</ProductNewRoomFormModelContextProvider>

			<DialogConfirmContinueCreateRoom
				roomType="STANDARD"
				open={openDialogConfirmContinueCreateRoom}
				handleOpenChange={handleOpenChange}
				hotelID={hotelID}
				onTransitionToCreateRoom={onTransitionToCreateRoom}
			/>
		</>
	);
};

NewRoomEditPresenter.displayName = "NewRoomEditPresenter";
